import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  JaarrekeningBalans,
  Onderneming,
  Ondernemingsvorm
} from '@snelstart-ventures/fiscaal/domain-logic';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {SpecOmzetbelastingPassivaComponent} from '../spec-omzetbelasting-passiva/spec-omzetbelasting-passiva.component';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SpecEgalisatiereserveComponent} from '../spec-egalisatiereserve/spec-egalisatiereserve.component';
import {SpecHirComponent} from '../spec-hir/spec-hir.component';
import {SpecForComponent} from '../spec-for/spec-for.component';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type BalansGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  waardeOpening: FormControl<number | null>;
  waardeSlot: FormControl<number | null>;
  nominaleWaarde: FormControl<number | null>;
  omzetbelastingDitBoekjaar: FormControl<number | null>;
  omzetbelastingVorigBoekjaar: FormControl<number | null>;
  omzetbelastingOudereBoekjaren: FormControl<number | null>;
}>;

type TotalGroup = FormGroup<{
  totaalOpening: FormControl<number | null>;
  totaalSlot: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-balans-passiva',
  templateUrl: './balans-passiva.component.html',
  imports: [
    SpecOmzetbelastingPassivaComponent,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SpecEgalisatiereserveComponent,
    SpecHirComponent,
    SpecForComponent,
    CommonModule
  ],
  styleUrls: ['./balans-passiva.component.scss']
})
export class BalansPassivaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public heeftOndernemingen = false;
  public heeftSamenwerkingsverband = false;
  public labelBalansOpening = '';
  public labelBalansSlot = '';
  public isPartner = false;
  public totaalActivaOpening: any;
  public totaalActivaSlot: any;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public eigenVermogenFg: BalansGroup = this.createJaarrekeningGroup();
  public kostenEgalisatieReserveFg: BalansGroup = this.createJaarrekeningGroup();
  public herinvesteringsReserveFg: BalansGroup = this.createJaarrekeningGroup();
  public oudedagesReserveaFg: BalansGroup = this.createJaarrekeningGroup();
  public terugkeerReserveFg: BalansGroup = this.createJaarrekeningGroup();
  public garantieVoorzieningFg: BalansGroup = this.createJaarrekeningGroup();
  public overigeVoorzieningenFg: BalansGroup = this.createJaarrekeningGroup();
  public schuldenLanglopendKredietinstellingenFg: BalansGroup = this.createJaarrekeningGroup();
  public schuldenLanglopendOverigFg: BalansGroup = this.createJaarrekeningGroup();
  public crediteurenFg: BalansGroup = this.createJaarrekeningGroup();
  public schuldenOmzetbelastingFg: BalansGroup = this.createJaarrekeningGroup();
  public schuldenLoonheffingenFg: BalansGroup = this.createJaarrekeningGroup();
  public schuldenKortlopendOverigFg: BalansGroup = this.createJaarrekeningGroup();

  public totalFg: TotalGroup = new FormGroup({
    totaalOpening: new FormControl<number | null>(null),
    totaalSlot: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    [
      this.eigenVermogenFg,
      this.kostenEgalisatieReserveFg,
      this.herinvesteringsReserveFg,
      this.oudedagesReserveaFg,
      this.terugkeerReserveFg,
      this.garantieVoorzieningFg,
      this.overigeVoorzieningenFg,
      this.schuldenLanglopendKredietinstellingenFg,
      this.schuldenLanglopendOverigFg,
      this.crediteurenFg,
      this.schuldenOmzetbelastingFg,
      this.schuldenLoonheffingenFg,
      this.schuldenKortlopendOverigFg,
    ].forEach(fg => {
      const waardeOpeningControl = fg.controls.waardeOpening;
      waardeOpeningControl?.valueChanges.subscribe(val => {
        if (val !== null) {
          this.saveForm();
        }
      });
      const waardeSlotControl = fg.controls.waardeSlot;
      waardeSlotControl?.valueChanges.subscribe(val => {
        if (val !== null) {
          this.saveForm();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.dataService.calculateLatestIncomeTaxReturn();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
      this.getLabels(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroups();
    this.heeftOndernemingen = false;
    this.heeftSamenwerkingsverband = false;
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private createJaarrekeningGroup(): BalansGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(null),
      waardeOpening: new FormControl<number | null>(null),
      waardeSlot: new FormControl<number | null>(null),
      nominaleWaarde: new FormControl<number | null>(null),
      omzetbelastingDitBoekjaar: new FormControl<number | null>(null),
      omzetbelastingVorigBoekjaar: new FormControl<number | null>(null),
      omzetbelastingOudereBoekjaren: new FormControl<number | null>(null),
    });
  }

  private resetFormGroups() {
    this.eigenVermogenFg.reset({omschrijving: 'goodwill'}, {emitEvent: false});
    this.kostenEgalisatieReserveFg.reset({omschrijving: 'egalisatieReserve'}, {emitEvent: false});
    this.herinvesteringsReserveFg.reset({omschrijving: 'herinvesteringsReserve'}, {emitEvent: false});
    this.oudedagesReserveaFg.reset({omschrijving: 'oudedagsReserve'}, {emitEvent: false});
    this.garantieVoorzieningFg.reset({omschrijving: 'garantieVoorziening'}, {emitEvent: false});
    this.overigeVoorzieningenFg.reset({omschrijving: 'overigeVoorzieningen'}, {emitEvent: false});
    this.schuldenLanglopendKredietinstellingenFg.reset({omschrijving: 'kredietinstellingen'}, {emitEvent: false});
    this.schuldenLanglopendOverigFg.reset({omschrijving: 'langlopendeSchulden'}, {emitEvent: false});
    this.crediteurenFg.reset({omschrijving: 'crediteuren'}, {emitEvent: false});
    this.schuldenOmzetbelastingFg.reset({omschrijving: 'omzetbelastingSchuld'}, {emitEvent: false});
    this.schuldenLoonheffingenFg.reset({omschrijving: 'loonheffingSchuld'}, {emitEvent: false});
    this.schuldenKortlopendOverigFg.reset({omschrijving: 'kortlopendeSchulden'}, {emitEvent: false});
    this.totalFg.reset({}, {emitEvent: false});
  }

  private patchFormGroups(ondernemingen: Onderneming[]): void {
    this.resetFormGroups();
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    this.heeftOndernemingen = true;
    this.heeftSamenwerkingsverband = onderneming.ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;

    if (!onderneming) return;

    // eigen vermogen reserves
    const egalisatieReserve = onderneming.eigenVermogen?.find(item => item.omschrijving === 'egalisatieReserve') ?? this.createJaarrekeningBalans('egalisatieReserve');
    const herinvesteringsreserve = onderneming.eigenVermogen?.find(item => item.omschrijving === 'herinvesteringsReserve') ?? this.createJaarrekeningBalans('herinvesteringsReserve');
    const oudedagsreserve = onderneming.eigenVermogen?.find(item => item.omschrijving === 'oudedagsReserve') ?? this.createJaarrekeningBalans('oudedagsReserve');
    this.kostenEgalisatieReserveFg.patchValue(egalisatieReserve, {emitEvent: false});
    this.herinvesteringsReserveFg.patchValue(herinvesteringsreserve, {emitEvent: false});
    this.oudedagesReserveaFg.patchValue(oudedagsreserve, {emitEvent: false});

    // voorzieningen
    const garantieVoorziening = onderneming.voorzieningen?.find(item => item.omschrijving === 'garantieVoorziening') ?? this.createJaarrekeningBalans('garantieVoorziening');
    const overigeVoorzieningen = onderneming.voorzieningen?.find(item => item.omschrijving === 'overigeVoorzieningen') ?? this.createJaarrekeningBalans('overigeVoorzieningen');
    this.garantieVoorzieningFg.patchValue(garantieVoorziening, {emitEvent: false});
    this.overigeVoorzieningenFg.patchValue(overigeVoorzieningen, {emitEvent: false});

    // langlopende schulden
    const kredietinstellingen = onderneming.langlopendeSchulden?.find(item => item.omschrijving === 'kredietinstellingen') ?? this.createJaarrekeningBalans('kredietinstellingen');
    const langlopendeSchulden = onderneming.langlopendeSchulden?.find(item => item.omschrijving === 'langlopendeSchulden') ?? this.createJaarrekeningBalans('langlopendeSchulden');
    this.schuldenLanglopendKredietinstellingenFg.patchValue(kredietinstellingen, {emitEvent: false});
    this.schuldenLanglopendOverigFg.patchValue(langlopendeSchulden, {emitEvent: false});

    // kortlopende schulden
    const crediteuren = onderneming.kortlopendeSchulden?.find(item => item.omschrijving === 'crediteuren') ?? this.createJaarrekeningBalansKortlopendeSchulden('crediteuren');
    const omzetbelastingSchuld = onderneming.kortlopendeSchulden?.find(item => item.omschrijving === 'omzetbelastingSchuld') ?? this.createJaarrekeningBalansKortlopendeSchulden('omzetbelastingSchuld');
    const loonheffingSchuld = onderneming.kortlopendeSchulden?.find(item => item.omschrijving === 'loonheffingSchuld') ?? this.createJaarrekeningBalansKortlopendeSchulden('loonheffingSchuld');
    const kortlopendeSchulden = onderneming.kortlopendeSchulden?.find(item => item.omschrijving === 'kortlopendeSchulden') ?? this.createJaarrekeningBalansKortlopendeSchulden('kortlopendeSchulden');
    this.crediteurenFg.patchValue(crediteuren, {emitEvent: false});
    this.schuldenOmzetbelastingFg.patchValue(omzetbelastingSchuld, {emitEvent: false});
    this.schuldenLoonheffingenFg.patchValue(loonheffingSchuld, {emitEvent: false});
    this.schuldenKortlopendOverigFg.patchValue(kortlopendeSchulden, {emitEvent: false});

    this.eigenVermogenFg.disable({onlySelf: true});
    let eigenVermogen = onderneming.eigenVermogen?.find(item => item.omschrijving === 'eigenVermogen');
    if (eigenVermogen) {
      eigenVermogen.waardeSlot = this.berekenEigenVermogenSlot(onderneming);
      eigenVermogen.waardeOpening = this.berekenEigenVermogenOpening(onderneming);
    } else {
      if (!onderneming.eigenVermogen) {
        onderneming.eigenVermogen = [];
      }
      eigenVermogen = this.createJaarrekeningBalans('eigenVermogen');
      eigenVermogen.waardeSlot = this.berekenEigenVermogenSlot(onderneming);
      eigenVermogen.waardeOpening = this.berekenEigenVermogenOpening(onderneming);
      onderneming.eigenVermogen.push(eigenVermogen);
    }
    this.eigenVermogenFg.patchValue(eigenVermogen, {emitEvent: false});
  }

  getLabels(data: Aangifte) {
    let begindatum;
    if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.startOnderneming != null) {
      begindatum = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].startOnderneming!;
    } else {
      begindatum = new Date(data.aangifteJaar, 0, 1);
    }
    let slotdatum;
    if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.datumStaking != null) {
      slotdatum = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].datumStaking!
    } else {
      slotdatum = new Date(data.aangifteJaar, 11, 31);
    }
    this.labelBalansOpening = begindatum != null
      ? `Balans ${new Date(begindatum).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })}`
      : 'Openingsbalans';
    this.labelBalansSlot = slotdatum != null
      ? `Balans ${new Date(slotdatum).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })}`
      : 'Slotbalans';
  }

  saveForm(): void {
    const idx = this.indexOnderneming;

    const eigenVermogenUpdates = [
      this.eigenVermogenFg.getRawValue(),
      this.kostenEgalisatieReserveFg.getRawValue(),
      this.herinvesteringsReserveFg.getRawValue(),
      this.oudedagesReserveaFg.getRawValue(),
      this.terugkeerReserveFg.getRawValue(),
    ];
    const voorzieningenUpdates = [
      this.garantieVoorzieningFg.getRawValue(),
      this.overigeVoorzieningenFg.getRawValue(),
    ];
    const langlopendeSchuldenUpdates = [
      this.schuldenLanglopendKredietinstellingenFg.getRawValue(),
      this.schuldenLanglopendOverigFg.getRawValue(),
    ];
    const kortlopendeSchuldenUpdates = [
      this.crediteurenFg.getRawValue(),
      this.schuldenOmzetbelastingFg.getRawValue(),
      this.schuldenLoonheffingenFg.getRawValue(),
      this.schuldenKortlopendOverigFg.getRawValue(),
    ];

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      // Merge form values with existing entity data, preserving non-null fields managed by
      // spec sub-components (e.g. fields set via spec dialogs) that are null in this
      // component's form due to not being re-patched after a spec save.
      const mergeWithExisting = (existingArray: any[] = [], updates: any[]) =>
        updates.map(formItem => {
          const existing = existingArray.find((e: any) => e.omschrijving === formItem.omschrijving) ?? {};
          return {
            ...existing,
            ...Object.fromEntries(Object.entries(formItem).filter(([, v]) => v !== null))
          };
        });

      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          eigenVermogen: mergeWithExisting(onderneming.eigenVermogen, eigenVermogenUpdates),
          voorzieningen: mergeWithExisting(onderneming.voorzieningen, voorzieningenUpdates),
          langlopendeSchulden: mergeWithExisting(onderneming.langlopendeSchulden, langlopendeSchuldenUpdates),
          kortlopendeSchulden: mergeWithExisting(onderneming.kortlopendeSchulden, kortlopendeSchuldenUpdates),
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!)
      } : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }

  berekenEigenVermogenSlot(onderneming: Onderneming): any {
    let totaalActiva = 0;
    let totaalPassivaZonderEv = 0;
    //immateriele vaste activa
    const goodWill = onderneming?.immaterieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'goodwill')?.waardeSlot || 0;
    const immaterieleVasteActiva = onderneming?.immaterieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'immaterieleVasteActiva')?.waardeSlot || 0;
    const bedrijfsGebouwen = onderneming?.materieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'bedrijfsGebouwen')?.waardeSlot || 0;
    const machinesEnInstallaties = onderneming?.materieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'machinesEnInstallaties')?.waardeSlot || 0;
    const andereVasteActiva = onderneming?.materieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'andereVasteActiva')?.waardeSlot || 0;
    const financieleVasteActiva = onderneming?.financieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'financieleVasteActiva')?.waardeSlot || 0;
    const onderhandenWerk = onderneming?.vlottendeActivaVoorraad?.filter(item => item !== null).find(item => item.omschrijving === 'onderhandenWerk')?.waardeSlot || 0;
    const voorraad = onderneming?.vlottendeActivaVoorraad?.filter(item => item !== null).find(item => item.omschrijving === 'voorraad')?.waardeSlot || 0;
    const debiteuren = onderneming?.vlottendeActivaVorderingen?.filter(item => item !== null).find(item => item.omschrijving === 'debiteuren')?.waardeSlot || 0;
    const omzetbelastingVordering = onderneming?.vlottendeActivaVorderingen?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingVordering')?.waardeSlot || 0;
    const overigeVorderingen = onderneming?.vlottendeActivaVorderingen?.filter(item => item !== null).find(item => item.omschrijving === 'overigeVorderingen')?.waardeSlot || 0;
    const effecten = onderneming?.effecten?.filter(item => item !== null).find(item => item.omschrijving === 'effecten')?.waardeSlot || 0;
    const liquideMiddelen = onderneming?.liquideMiddelen?.filter(item => item !== null).find(item => item.omschrijving === 'liquideMiddelen')?.waardeSlot || 0;

    totaalActiva = goodWill + immaterieleVasteActiva + bedrijfsGebouwen + machinesEnInstallaties + andereVasteActiva + financieleVasteActiva + voorraad +
      onderhandenWerk + debiteuren + omzetbelastingVordering + overigeVorderingen + effecten + liquideMiddelen;
    const egalisatieReserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'egalisatieReserve')?.waardeSlot || 0;
    const oudedagsreserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'oudedagsReserve')?.waardeSlot || 0;
    const herinvesteringsreserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'herinvesteringsReserve')?.waardeSlot || 0;
    const garantieVoorziening = onderneming?.voorzieningen?.filter(item => item !== null).find(item => item.omschrijving === 'garantieVoorziening')?.waardeSlot || 0;
    const overigeVoorzieningen = onderneming?.voorzieningen?.filter(item => item !== null).find(item => item.omschrijving === 'overigeVoorzieningen')?.waardeSlot || 0;
    const kredietinstellingen = onderneming?.langlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'kredietinstellingen')?.waardeSlot || 0;
    const langlopendeSchulden = onderneming?.langlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'langlopendeSchulden')?.waardeSlot || 0;
    const crediteuren = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'crediteuren')?.waardeSlot || 0;
    const omzetbelastingSchuld = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingSchuld')?.waardeSlot || 0;
    const loonheffingSchuld = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'loonheffingSchuld')?.waardeSlot || 0;
    const kortlopendeSchulden = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'kortlopendeSchulden')?.waardeSlot || 0;

    totaalPassivaZonderEv = egalisatieReserve + oudedagsreserve + herinvesteringsreserve + garantieVoorziening + overigeVoorzieningen + kredietinstellingen + langlopendeSchulden +
      crediteuren + loonheffingSchuld + omzetbelastingSchuld + kortlopendeSchulden;
    this.totaalActivaSlot = totaalActiva
    return totaalActiva - totaalPassivaZonderEv;
  }

  berekenEigenVermogenOpening(onderneming: Onderneming): any {
    let totaalActiva = 0;
    let totaalPassivaZonderEv = 0;
    //immateriele vaste activa
    const goodWill = onderneming?.immaterieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'goodwill')?.waardeOpening || 0;
    const immaterieleVasteActiva = onderneming?.immaterieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'immaterieleVasteActiva')?.waardeOpening || 0;
    const bedrijfsGebouwen = onderneming?.materieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'bedrijfsGebouwen')?.waardeOpening || 0;
    const machinesEnInstallaties = onderneming?.materieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'machinesEnInstallaties')?.waardeOpening || 0;
    const andereVasteActiva = onderneming?.materieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'andereVasteActiva')?.waardeOpening || 0;
    const financieleVasteActiva = onderneming?.financieleVasteActiva?.filter(item => item !== null).find(item => item.omschrijving === 'financieleVasteActiva')?.waardeOpening || 0;
    const onderhandenWerk = onderneming?.vlottendeActivaVoorraad?.filter(item => item !== null).find(item => item.omschrijving === 'onderhandenWerk')?.waardeOpening || 0;
    const voorraad = onderneming?.vlottendeActivaVoorraad?.filter(item => item !== null).find(item => item.omschrijving === 'voorraad')?.waardeOpening || 0;
    const debiteuren = onderneming?.vlottendeActivaVorderingen?.filter(item => item !== null).find(item => item.omschrijving === 'debiteuren')?.waardeOpening || 0;
    const omzetbelastingVordering = onderneming?.vlottendeActivaVorderingen?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingVordering')?.waardeOpening || 0;
    const overigeVorderingen = onderneming?.vlottendeActivaVorderingen?.filter(item => item !== null).find(item => item.omschrijving === 'overigeVorderingen')?.waardeOpening || 0;
    const effecten = onderneming?.effecten?.filter(item => item !== null).find(item => item.omschrijving === 'effecten')?.waardeOpening || 0;
    const liquideMiddelen = onderneming?.liquideMiddelen?.filter(item => item !== null).find(item => item.omschrijving === 'liquideMiddelen')?.waardeOpening || 0;

    totaalActiva = goodWill + immaterieleVasteActiva + bedrijfsGebouwen + machinesEnInstallaties + andereVasteActiva + financieleVasteActiva + voorraad +
      onderhandenWerk + debiteuren + omzetbelastingVordering + overigeVorderingen + effecten + liquideMiddelen;

    const egalisatieReserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'egalisatieReserve')?.waardeOpening || 0;
    const oudedagsreserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'oudedagsReserve')?.waardeOpening || 0;
    const herinvesteringsreserve = onderneming?.eigenVermogen?.filter(item => item !== null).find(item => item.omschrijving === 'herinvesteringsReserve')?.waardeOpening || 0;
    const garantieVoorziening = onderneming?.voorzieningen?.filter(item => item !== null).find(item => item.omschrijving === 'garantieVoorziening')?.waardeOpening || 0;
    const overigeVoorzieningen = onderneming?.voorzieningen?.filter(item => item !== null).find(item => item.omschrijving === 'overigeVoorzieningen')?.waardeOpening || 0;
    const kredietinstellingen = onderneming?.langlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'kredietinstellingen')?.waardeOpening || 0;
    const langlopendeSchulden = onderneming?.langlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'langlopendeSchulden')?.waardeOpening || 0;
    const crediteuren = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'crediteuren')?.waardeOpening || 0;
    const omzetbelastingSchuld = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'omzetbelastingSchuld')?.waardeOpening || 0;
    const loonheffingSchuld = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'loonheffingSchuld')?.waardeOpening || 0;
    const kortlopendeSchulden = onderneming?.kortlopendeSchulden?.filter(item => item !== null).find(item => item.omschrijving === 'kortlopendeSchulden')?.waardeOpening || 0;

    totaalPassivaZonderEv = egalisatieReserve + oudedagsreserve + herinvesteringsreserve + garantieVoorziening + overigeVoorzieningen + kredietinstellingen + langlopendeSchulden +
      crediteuren + loonheffingSchuld + omzetbelastingSchuld + kortlopendeSchulden;
    this.totaalActivaOpening = totaalActiva
    return totaalActiva - totaalPassivaZonderEv;
  }

  public getTotaalEvOpening(): any {
    const sum =
      (this.kostenEgalisatieReserveFg?.get("waardeOpening")?.value ?? 0) +
      (this.herinvesteringsReserveFg?.get("waardeOpening")?.value ?? 0) +
      (this.oudedagesReserveaFg?.get("waardeOpening")?.value ?? 0) +
      (this.terugkeerReserveFg?.get("waardeOpening")?.value ?? 0) +
      (this.garantieVoorzieningFg?.get("waardeOpening")?.value ?? 0) +
      (this.overigeVoorzieningenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenLanglopendKredietinstellingenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenLanglopendOverigFg?.get("waardeOpening")?.value ?? 0) +
      (this.crediteurenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenOmzetbelastingFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenLoonheffingenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenKortlopendOverigFg?.get("waardeOpening")?.value ?? 0);
    this.eigenVermogenFg.patchValue({ waardeOpening: this.totaalActivaOpening - sum }, { emitEvent: false });
    return sum;
  }

  public getTotaalEvSlot(): any {
    const sum =
      (this.kostenEgalisatieReserveFg?.get("waardeSlot")?.value ?? 0) +
      (this.herinvesteringsReserveFg?.get("waardeSlot")?.value ?? 0) +
      (this.oudedagesReserveaFg?.get("waardeSlot")?.value ?? 0) +
      (this.terugkeerReserveFg?.get("waardeSlot")?.value ?? 0) +
      (this.garantieVoorzieningFg?.get("waardeSlot")?.value ?? 0) +
      (this.overigeVoorzieningenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenLanglopendKredietinstellingenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenLanglopendOverigFg?.get("waardeSlot")?.value ?? 0) +
      (this.crediteurenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenOmzetbelastingFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenLoonheffingenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenKortlopendOverigFg?.get("waardeSlot")?.value ?? 0);
    this.eigenVermogenFg.patchValue({ waardeSlot: this.totaalActivaSlot - sum }, { emitEvent: false });
    return sum;
  }

  public getTotaalPassivaOpening(): any {
    const sum =
      (this.eigenVermogenFg?.get("waardeOpening")?.value ?? 0) +
      (this.kostenEgalisatieReserveFg?.get("waardeOpening")?.value ?? 0) +
      (this.herinvesteringsReserveFg?.get("waardeOpening")?.value ?? 0) +
      (this.oudedagesReserveaFg?.get("waardeOpening")?.value ?? 0) +
      (this.terugkeerReserveFg?.get("waardeOpening")?.value ?? 0) +
      (this.garantieVoorzieningFg?.get("waardeOpening")?.value ?? 0) +
      (this.overigeVoorzieningenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenLanglopendKredietinstellingenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenLanglopendOverigFg?.get("waardeOpening")?.value ?? 0) +
      (this.crediteurenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenOmzetbelastingFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenLoonheffingenFg?.get("waardeOpening")?.value ?? 0) +
      (this.schuldenKortlopendOverigFg?.get("waardeOpening")?.value ?? 0);
    this.totalFg.patchValue({ totaalOpening: sum }, { emitEvent: false });
    return sum;
  }

  public getTotaalPassivaSlot(): any {
    const sum =
      (this.eigenVermogenFg?.get("waardeSlot")?.value ?? 0) +
      (this.kostenEgalisatieReserveFg?.get("waardeSlot")?.value ?? 0) +
      (this.herinvesteringsReserveFg?.get("waardeSlot")?.value ?? 0) +
      (this.oudedagesReserveaFg?.get("waardeSlot")?.value ?? 0) +
      (this.terugkeerReserveFg?.get("waardeSlot")?.value ?? 0) +
      (this.garantieVoorzieningFg?.get("waardeSlot")?.value ?? 0) +
      (this.overigeVoorzieningenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenLanglopendKredietinstellingenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenLanglopendOverigFg?.get("waardeSlot")?.value ?? 0) +
      (this.crediteurenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenOmzetbelastingFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenLoonheffingenFg?.get("waardeSlot")?.value ?? 0) +
      (this.schuldenKortlopendOverigFg?.get("waardeSlot")?.value ?? 0);
    this.totalFg.patchValue({ totaalSlot: sum }, { emitEvent: false });
    return sum;
  }

  private createJaarrekeningBalans(omschrijving: string): JaarrekeningBalans {
    return {
      omschrijving: omschrijving,
      waardeOpening: undefined as unknown as number,
      waardeSlot: undefined as unknown as number,
      aanschafWaarde: undefined as unknown as number,
      restWaarde: undefined as unknown as number,
      nominaleWaarde: 0,
      bodemwaarde: undefined as unknown as number,
      omzetbelastingDitBoekjaar: undefined as unknown as number,
      omzetbelastingVorigBoekjaar: undefined as unknown as number,
      omzetbelastingOudereBoekjaren: undefined as unknown as number
    } as JaarrekeningBalans;
  }

  private createJaarrekeningBalansKortlopendeSchulden(omschrijving: string): JaarrekeningBalans {
    return {
      omschrijving: omschrijving,
      waardeOpening: undefined as unknown as number,
      waardeSlot: undefined as unknown as number,
      aanschafWaarde: undefined as unknown as number,
      restWaarde: undefined as unknown as number,
      nominaleWaarde: 0,
      bodemwaarde: undefined as unknown as number,
      omzetbelastingDitBoekjaar: undefined as unknown as number,
      omzetbelastingVorigBoekjaar: undefined as unknown as number,
      omzetbelastingOudereBoekjaren: undefined as unknown as number
    } as JaarrekeningBalans;
  }
}
